import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useDataProvider, useDebouncedEvent, useTranslate } from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import type { CrmDataProvider } from "../providers/types";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { SearchResourceName, SearchResult } from "../types";
import { useRecentSearches, type RecentSearch } from "./recentSearches";
import {
  DESKTOP_SEARCH_RESOURCES,
  MOBILE_SEARCH_RESOURCES,
  getSearchResultUrl,
  getSearchTerms,
  labelSearchResult,
} from "./searchModel";

export const MIN_SEARCH_LENGTH = 2;
// Short enough to feel instant, long enough to skip most mid-word requests.
const DEBOUNCE_MS = 200;
const MAX_RESULTS_PER_RESOURCE = 10;

/**
 * Run the global search for an already debounced search term.
 *
 * Stays idle until the term reaches MIN_SEARCH_LENGTH, so opening the dialog
 * does not fire a request. `resources` restricts the query server-side to the
 * resources the current layout can actually link to. The row cap applies to
 * each resource, so every group shows its own newest matches. The previous
 * results stay on screen while the next term loads, so typing never flashes
 * a loading state.
 */
const useGlobalSearch = (
  debouncedQuery: string,
  resources: readonly SearchResourceName[],
) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const isEnabled = debouncedQuery.length >= MIN_SEARCH_LENGTH;

  const { data, isPending, isFetching, error, isPlaceholderData } = useQuery({
    queryKey: ["globalSearch", debouncedQuery, resources],
    queryFn: () =>
      dataProvider.globalSearch(
        debouncedQuery,
        resources,
        MAX_RESULTS_PER_RESOURCE,
      ),
    enabled: isEnabled,
    placeholderData: keepPreviousData,
    meta: { persist: false },
    // offlineFirst (the app default) pauses a failed search while offline,
    // leaving the dialog loading forever instead of reporting the error
    networkMode: "always",
  });

  return {
    results: isEnabled ? (data ?? []) : [],
    isPending: isEnabled && isPending,
    isFetching: isEnabled && isFetching,
    error: isPlaceholderData ? null : error,
  };
};

export type LinkedResult = { result: SearchResult; url: string };

/**
 * State shared by the mobile overlay and the desktop palette: the typed and
 * debounced query, the linked results, expanded groups and recent searches.
 */
export const useSearchController = ({
  open,
  isMobile,
  onOpenChange,
}: {
  open: boolean;
  isMobile: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<
    ReadonlySet<SearchResourceName>
  >(new Set());
  const updateDebouncedQuery = useDebouncedEvent(
    setDebouncedQuery,
    DEBOUNCE_MS,
  );
  const { results, isPending, isFetching, error } = useGlobalSearch(
    debouncedQuery,
    isMobile ? MOBILE_SEARCH_RESOURCES : DESKTOP_SEARCH_RESOURCES,
  );
  const recents = useRecentSearches();
  const { refresh: refreshRecents } = recents;

  useEffect(() => {
    if (open) refreshRecents();
  }, [open, refreshRecents]);

  // Resources are already scoped to this layout server-side; this only drops
  // the rare row whose parent record is missing.
  const translate = useTranslate();
  const { companySectors, dealCategories, taskTypes } =
    useConfigurationContext();
  const linkedResults = useMemo(
    () =>
      results.reduce<LinkedResult[]>((acc, result) => {
        const url = getSearchResultUrl(result, isMobile);
        if (!url) return acc;
        const labeled = labelSearchResult(
          result,
          { companySectors, dealCategories, taskTypes },
          translate,
        );
        return [...acc, { result: labeled, url }];
      }, []),
    [results, isMobile, companySectors, dealCategories, taskTypes, translate],
  );

  const terms = useMemo(() => getSearchTerms(debouncedQuery), [debouncedQuery]);
  const isTooShort = debouncedQuery.length < MIN_SEARCH_LENGTH;

  const changeQuery = (value: string) => {
    setQuery(value);
    setExpandedGroups(new Set());
    // Cleared: back to the empty state at once, nothing to wait for.
    if (value.trim().length < MIN_SEARCH_LENGTH) {
      setDebouncedQuery(value.trim());
    }
    updateDebouncedQuery(value.trim());
  };

  /** Re-run a recent search without waiting for the debounce. */
  const runQuery = (value: string) => {
    setQuery(value);
    setExpandedGroups(new Set());
    setDebouncedQuery(value);
    updateDebouncedQuery(value);
  };

  const rememberQuery = () => {
    if (!isTooShort && linkedResults.length > 0) {
      recents.remember({ type: "query", query: debouncedQuery });
    }
  };

  const close = () => {
    setQuery("");
    setDebouncedQuery("");
    setExpandedGroups(new Set());
    // replaces an update still pending from the last keystroke
    updateDebouncedQuery("");
    onOpenChange(false);
  };

  const openUrl = (url: string, entry?: RecentSearch) => {
    if (entry) recents.remember(entry);
    close();
    navigate(url);
  };

  const openResult = ({ result, url }: LinkedResult, title: string) =>
    openUrl(url, { type: "result", resource: result.resource, title, url });

  const expandGroup = (name: SearchResourceName) =>
    setExpandedGroups((groups) => new Set([...groups, name]));

  return {
    query,
    debouncedQuery,
    terms,
    isTooShort,
    isPending,
    isFetching,
    error,
    linkedResults,
    expandedGroups,
    expandGroup,
    changeQuery,
    runQuery,
    rememberQuery,
    close,
    openUrl,
    openResult,
    recents,
  };
};

export type SearchController = ReturnType<typeof useSearchController>;
